import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  FindAllProgramsPayload,
  ProgramPayload,
  ProgramsService,
  programService,
} from "@/services/program.service";
import { useProgramsByUser } from "./useUsers";

const PROGRAMS_LIST_KEY = "pret-programs-list";
const PROGRAM_BY_ID_KEY = "pret-program-by-id";

// ---------------------------------------------------------------------------
// Legacy hooks (DO NOT REMOVE) - used by other screens
// ---------------------------------------------------------------------------

export const useGetPrograms = ({ sortType = "asc" }: { sortType?: string }) => {
  const {
    data: programs,
    isLoading: isLoadingPrograms,
    isFetching: isFetchingPrograms,
    refetch: refetchPrograms,
  } = useQuery({
    queryKey: ["get-programs"],
    queryFn: () => programService.getPrograms({ sortType }),
    staleTime: 1 * 60 * 60 * 1000,
  });

  return {
    programs,
    isLoadingPrograms,
    isFetchingPrograms,
    refetchPrograms,
  };
};

export const useGetUserPrograms = ({
  userId,
  sortType = "asc",
  hasSystemAdminRole,
}: {
  userId: string;
  sortType?: string;
  hasSystemAdminRole: boolean;
}) => {
  if (hasSystemAdminRole) {
    return useGetPrograms({ sortType });
  } else {
    return useProgramsByUser({ userId, sortType });
  }
};

// ---------------------------------------------------------------------------
// Full maintenance hooks
// ---------------------------------------------------------------------------

export const usePrograms = (filters: FindAllProgramsPayload) => {
  const {
    data: programsList,
    isLoading: isLoadingProgramsList,
    isFetching: isFetchingProgramsList,
    refetch: refetchProgramsList,
  } = useQuery({
    queryKey: [PROGRAMS_LIST_KEY, filters],
    queryFn: () => ProgramsService.findAll(filters),
  });

  return {
    programsList,
    isLoadingProgramsList,
    isFetchingProgramsList,
    refetchProgramsList,
  };
};

export const useProgram = (id: string, enabled = true) => {
  const {
    data: program,
    isLoading: isLoadingProgram,
    refetch: refetchProgram,
  } = useQuery({
    queryKey: [PROGRAM_BY_ID_KEY, id],
    queryFn: () => ProgramsService.findOne(id),
    enabled: !!id && enabled,
  });

  return {
    program,
    isLoadingProgram,
    refetchProgram,
  };
};

export const useCreateProgram = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ProgramPayload) => ProgramsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_LIST_KEY] });
    },
  });
};

export const useUpdateProgram = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ProgramPayload }) =>
      ProgramsService.update({ id, data }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_LIST_KEY] });
      queryClient.invalidateQueries({
        queryKey: [PROGRAM_BY_ID_KEY, variables.id],
      });
    },
  });
};

export const useArchiveProgram = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => ProgramsService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PROGRAMS_LIST_KEY] });
    },
  });
};
